/**
 * ============================================================
 * KEJJA OS — src/pages/routines/ManagePage.tsx
 * Create / edit / delete routines.
 * ============================================================
 */

import { useState, type ChangeEvent, type FormEvent } from 'react';
import { Plus, Trash2, Pencil, Users, User, Calendar } from 'lucide-react';

import { useRoutinesContext } from './RoutinesLayout';
import {
  useRoutines, useCreateRoutine, useUpdateRoutine, useDeleteRoutine,
} from '@/hooks/useRoutines';
import type { Routine, RoutineCadence, RoutineSchedule } from '@/lib/routinesApi';
import EmojiPicker from '@/components/EmojiPicker';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const WEEKDAY_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const PALETTE = ['#14b8a6', '#0891b2', '#3b82f6', '#8b5cf6', '#ec4899', '#f97316', '#10b981', '#eab308'];

interface FormState {
  id: string | null;
  name: string;
  description: string;
  isShared: boolean;
  cadence: RoutineCadence;
  weeklyDay: number;        // 0-6
  customWeekdays: number[]; // array of 0-6
  monthlyDay: number;       // 1-31
  timeOfDay: string;        // HH:MM or ''
  icon: string;
  color: string;
}

const EMPTY_FORM: FormState = {
  id: null,
  name: '',
  description: '',
  isShared: true,
  cadence: 'daily',
  weeklyDay: 1,
  customWeekdays: [1, 3, 5],
  monthlyDay: 1,
  timeOfDay: '',
  icon: '',
  color: PALETTE[0]!,
};

export default function ManagePage() {
  const { homeId } = useRoutinesContext();
  const { data, isLoading } = useRoutines(homeId);
  const create = useCreateRoutine(homeId);
  const update = useUpdateRoutine(homeId);
  const remove = useDeleteRoutine(homeId);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [err, setErr] = useState<string | null>(null);

  const openNew = () => {
    setForm(EMPTY_FORM);
    setShowForm(true);
    setErr(null);
  };

  const openEdit = (r: Routine) => {
    const sched = r.scheduleJson ?? {};
    setForm({
      id: r.id,
      name: r.name,
      description: r.description ?? '',
      isShared: r.userId === null,
      cadence: r.cadence,
      weeklyDay: sched.dayOfWeek ?? 1,
      customWeekdays: sched.weekdays ?? [1, 3, 5],
      monthlyDay: sched.day ?? 1,
      timeOfDay: r.timeOfDay ?? '',
      icon: r.icon ?? '',
      color: r.color ?? PALETTE[0]!,
    });
    setShowForm(true);
    setErr(null);
  };

  const buildSchedule = (): RoutineSchedule => {
    switch (form.cadence) {
      case 'daily': return {};
      case 'weekly': return { dayOfWeek: form.weeklyDay };
      case 'custom_weekdays': return { weekdays: form.customWeekdays.slice().sort() };
      case 'monthly': return { day: form.monthlyDay };
    }
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!form.name.trim()) { setErr('Name is required.'); return; }
    if (form.cadence === 'custom_weekdays' && form.customWeekdays.length === 0) {
      setErr('Pick at least one weekday.'); return;
    }

    const body = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      cadence: form.cadence,
      scheduleJson: buildSchedule(),
      timeOfDay: form.timeOfDay || null,
      icon: form.icon || null,
      color: form.color,
      userId: form.isShared ? null : undefined,  // undefined = server keeps existing on update
    };

    const onDone = () => { setShowForm(false); setForm(EMPTY_FORM); };
    const onError = (e: Error) => setErr(e.message ?? 'Could not save.');

    if (form.id) {
      update.mutate({ routineId: form.id, patch: body }, { onSuccess: onDone, onError });
    } else {
      create.mutate(body, { onSuccess: onDone, onError });
    }
  };

  const handleDelete = (r: Routine) => {
    if (confirm(`Delete "${r.name}"? Check-in history will also be removed.`)) {
      remove.mutate(r.id);
    }
  };

  const toggleWeekday = (d: number) => {
    setForm({
      ...form,
      customWeekdays: form.customWeekdays.includes(d)
        ? form.customWeekdays.filter((x) => x !== d)
        : [...form.customWeekdays, d],
    });
  };

  return (
    <div className="space-y-5 pb-12">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-foreground">All routines</h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            {data?.routines.length ?? 0} routine{data?.routines.length === 1 ? '' : 's'}
          </p>
        </div>
        <Button
          size="sm" className="bg-teal-600 hover:bg-teal-700 text-white"
          onClick={showForm ? () => setShowForm(false) : openNew}
        >
          <Plus className="w-4 h-4 mr-1" />
          {showForm ? 'Close' : 'New routine'}
        </Button>
      </div>

      {showForm && (
        <Card className="border-teal-200 bg-teal-50/30 dark:bg-teal-950/10">
          <CardContent className="p-4">
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <Label htmlFor="r-name" className="text-xs">Name</Label>
                <Input
                  id="r-name"
                  placeholder="e.g. Morning prayer, Water plants"
                  value={form.name}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, name: e.target.value })}
                  className="mt-1"
                  autoFocus
                />
              </div>

              <div>
                <Label htmlFor="r-desc" className="text-xs">Description (optional)</Label>
                <Input
                  id="r-desc"
                  placeholder="What does this look like?"
                  value={form.description}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, description: e.target.value })}
                  className="mt-1"
                />
              </div>

              {/* Shared vs personal */}
              <div>
                <Label className="text-xs block mb-1.5">Who is this for?</Label>
                <div className="grid grid-cols-2 gap-1">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, isShared: true })}
                    className={cn(
                      'px-3 py-1.5 text-xs rounded-lg border font-medium flex items-center justify-center gap-1',
                      form.isShared
                        ? 'bg-teal-600 text-white border-teal-600'
                        : 'border-border bg-background hover:bg-muted',
                    )}
                  >
                    <Users className="w-3.5 h-3.5" /> Shared family
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, isShared: false })}
                    className={cn(
                      'px-3 py-1.5 text-xs rounded-lg border font-medium flex items-center justify-center gap-1',
                      !form.isShared
                        ? 'bg-teal-600 text-white border-teal-600'
                        : 'border-border bg-background hover:bg-muted',
                    )}
                  >
                    <User className="w-3.5 h-3.5" /> My personal
                  </button>
                </div>
              </div>

              {/* Cadence */}
              <div>
                <Label className="text-xs block mb-1.5">Cadence</Label>
                <div className="grid grid-cols-4 gap-1">
                  {(['daily', 'weekly', 'custom_weekdays', 'monthly'] as const).map((c) => (
                    <button
                      key={c} type="button"
                      onClick={() => setForm({ ...form, cadence: c })}
                      className={cn(
                        'px-2 py-1.5 text-[11px] rounded-lg border font-medium',
                        form.cadence === c
                          ? 'bg-teal-600 text-white border-teal-600'
                          : 'border-border bg-background hover:bg-muted',
                      )}
                    >
                      {c === 'custom_weekdays' ? 'Select days' : c[0]!.toUpperCase() + c.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Cadence sub-options */}
              {form.cadence === 'weekly' && (
                <div>
                  <Label className="text-xs block mb-1.5">Which day of the week?</Label>
                  <div className="grid grid-cols-7 gap-1">
                    {WEEKDAYS.map((w, i) => (
                      <button
                        key={i} type="button"
                        onClick={() => setForm({ ...form, weeklyDay: i })}
                        className={cn(
                          'h-9 text-xs rounded-lg border font-bold',
                          form.weeklyDay === i
                            ? 'bg-teal-600 text-white border-teal-600'
                            : 'border-border bg-background hover:bg-muted',
                        )}
                        title={WEEKDAY_FULL[i]}
                      >
                        {w}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {form.cadence === 'custom_weekdays' && (
                <div>
                  <Label className="text-xs block mb-1.5">Which days?</Label>
                  <div className="grid grid-cols-7 gap-1">
                    {WEEKDAYS.map((w, i) => (
                      <button
                        key={i} type="button"
                        onClick={() => toggleWeekday(i)}
                        className={cn(
                          'h-9 text-xs rounded-lg border font-bold',
                          form.customWeekdays.includes(i)
                            ? 'bg-teal-600 text-white border-teal-600'
                            : 'border-border bg-background hover:bg-muted',
                        )}
                        title={WEEKDAY_FULL[i]}
                      >
                        {w}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {form.cadence === 'monthly' && (
                <div>
                  <Label htmlFor="r-mday" className="text-xs">Day of the month</Label>
                  <Input
                    id="r-mday" type="number" min="1" max="31"
                    value={form.monthlyDay}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, monthlyDay: Number(e.target.value) || 1 })}
                    className="mt-1 w-24"
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">
                    If the month doesn't have that day (e.g. day 31 in Feb), it triggers on the last day.
                  </p>
                </div>
              )}

              {/* Time + visuals */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="r-time" className="text-xs">Time (optional)</Label>
                  <Input
                    id="r-time" type="time"
                    value={form.timeOfDay}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, timeOfDay: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Icon</Label>
                  <div className="mt-1">
                    <EmojiPicker
                      value={form.icon}
                      onChange={(emoji) => setForm({ ...form, icon: emoji })}
                      placeholder="🌅"
                      size="md"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-xs block mb-1.5">Color</Label>
                <div className="flex gap-2">
                  {PALETTE.map((c) => (
                    <button
                      key={c} type="button"
                      onClick={() => setForm({ ...form, color: c })}
                      className={cn(
                        'w-7 h-7 rounded-full border-2 transition-all',
                        form.color === c ? 'border-foreground scale-110' : 'border-transparent',
                      )}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
              </div>

              {err && <div className="text-xs text-rose-600 font-medium">{err}</div>}

              <div className="flex gap-2 pt-1">
                <Button
                  type="submit"
                  className="bg-teal-600 hover:bg-teal-700 text-white flex-1"
                  disabled={create.isPending || update.isPending}
                >
                  {create.isPending || update.isPending ? 'Saving…' : form.id ? 'Save changes' : 'Add routine'}
                </Button>
                <Button type="button" variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-16 rounded-xl" />
          <Skeleton className="h-16 rounded-xl" />
        </div>
      ) : (data?.routines ?? []).length === 0 && !showForm ? (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center">
            <Calendar className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">
              No routines yet. Head back to Today and try the starter pack, or add one above.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0 divide-y divide-border">
            {data?.routines.map((r) => (
              <RoutineManageRow
                key={r.id}
                routine={r}
                onEdit={() => openEdit(r)}
                onDelete={() => handleDelete(r)}
              />
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function RoutineManageRow({
  routine, onEdit, onDelete,
}: {
  routine: Routine;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const isShared = routine.userId === null;
  const sched = routine.scheduleJson ?? {};

  let cadenceLabel = 'Daily';
  if (routine.cadence === 'weekly') {
    cadenceLabel = `Weekly · ${WEEKDAY_FULL[sched.dayOfWeek ?? 1]}`;
  } else if (routine.cadence === 'custom_weekdays') {
    const days = (sched.weekdays ?? []).map((d: number) => WEEKDAYS[d]).join(' ');
    cadenceLabel = days || 'Custom';
  } else if (routine.cadence === 'monthly') {
    cadenceLabel = `Monthly · day ${sched.day ?? 1}`;
  }

  return (
    <div className="p-3 flex items-center gap-3">
      <div
        className="h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0 text-lg"
        style={{
          backgroundColor: routine.color ? `${routine.color}20` : 'var(--muted)',
          color: routine.color ?? 'var(--muted-foreground)',
        }}
      >
        {routine.icon || routine.name[0]?.toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm truncate">{routine.name}</span>
          <Badge variant="outline" className="text-[9px] h-4 px-1 font-normal">
            {isShared ? <Users className="w-2.5 h-2.5 mr-0.5" /> : <User className="w-2.5 h-2.5 mr-0.5" />}
            {isShared ? 'shared' : 'personal'}
          </Badge>
        </div>
        <div className="text-[11px] text-muted-foreground mt-0.5">
          {cadenceLabel}
          {routine.timeOfDay && ` · ${routine.timeOfDay}`}
        </div>
      </div>
      <div className="flex gap-1 flex-shrink-0">
        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={onEdit} aria-label="Edit">
          <Pencil className="w-3.5 h-3.5" />
        </Button>
        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-muted-foreground hover:text-rose-600" onClick={onDelete} aria-label="Delete">
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}
